import React, { useEffect, useRef } from "react";
import Style from "./PromoBanner.module.scss";

export const isVideoSlide = (slide) =>
  String(slide?.type || "").toLowerCase() === "video";

/**
 * One carousel slide - an image (including GIFs, which animate natively in <img>)
 * or a video. The media type comes from the API's `type` field rather than sniffing
 * the file extension, so CDN URLs without an extension still work.
 */
const BannerSlide = ({
  slide,
  isActive,
  isDesktop,
  reducedMotion,
  isOnlySlide,
  isFirst,
  onEnded,
  onError,
}) => {
  const videoRef = useRef(null);
  const isVideo = isVideoSlide(slide);

  const desktopUrl = slide.media_desktop_url;
  const mobileUrl = slide.media_mobile_url;
  const videoSrc = (isDesktop ? desktopUrl : mobileUrl) || desktopUrl || mobileUrl;
  const poster =
    (isDesktop ? slide.poster_desktop_url : slide.poster_mobile_url) ||
    slide.poster_desktop_url ||
    slide.poster_mobile_url;

  // Play only the slide that is on screen; everything else stays paused so we are not
  // decoding video the visitor cannot see. Reduced-motion viewers get the poster plus
  // native controls instead of an autoplaying clip.
  useEffect(() => {
    const video = videoRef.current;
    if (!isVideo || !video) return;

    if (isActive && !reducedMotion) {
      video.currentTime = 0;
      const played = video.play();
      // Browsers reject play() when autoplay is blocked - the poster stays visible.
      if (played && typeof played.catch === "function") played.catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, isVideo, reducedMotion]);

  let media;

  if (isVideo) {
    media = (
      <video
        ref={videoRef}
        className={Style.media}
        poster={poster || undefined}
        muted
        playsInline
        // iOS Safari needs all four of these to inline-play a muted autoplaying clip.
        webkit-playsinline="true"
        x5-video-player-type="h5"
        x-webkit-airplay="allow"
        preload="auto"
        loop={isOnlySlide && !reducedMotion}
        controls={reducedMotion}
        onEnded={onEnded}
        onError={onError}
        aria-label={slide.alt_text || undefined}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    );
  } else {
    media = (
      <picture>
        {desktopUrl && <source media="(min-width: 768px)" srcSet={desktopUrl} />}
        <img
          className={Style.media}
          src={mobileUrl || desktopUrl}
          alt={slide.alt_text || ""}
          loading={isFirst ? "eager" : "lazy"}
          decoding="async"
          onError={onError}
        />
      </picture>
    );
  }

  if (!slide.link_url) return media;

  const target = slide.link_target === "_blank" ? "_blank" : "_self";

  return (
    <a
      className={Style.slide_link}
      href={slide.link_url}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      // Only the visible slide should be reachable by keyboard.
      tabIndex={isActive ? 0 : -1}
      aria-hidden={isActive ? undefined : true}
    >
      {media}
    </a>
  );
};

export default BannerSlide;
